<template>
  <div>
    <form @submit.stop.prevent="form.handleSubmit()">
      <div v-if="isPrivateMessageType">
        <form.Field name="isUpdateNote">
          <template #default="{ field }">
            <BaseCheckbox
              :model-value="isUpdateNote"
              name="isUpdateNote"
              label="Project update note"
              description="Check if this message is a project update note"
              @update:model-value="(value) => setIsUpdateNote(value, field)"
            />
          </template>
        </form.Field>

        <BaseCheckbox
          v-if="isUpdateNote"
          v-model="includeRealtor"
          label="Include realtor"
          description="Check if you want to include the realtor in this message"
        />

        <!-- field required if isUpdateNote is false -->
        <form.Field
          name="to.id"
          :validators="{
            onChange: yup.string().test('required', 'Send to is required', (value) => {
              return isUpdateNote || !!value
            }),
            onChangeListenTo: ['isUpdateNote'],
          }"
        >
          <template #default="{ field }">
            <EmployeeAutoComplete
              :model-value="field.state.value"
              name="send_to"
              label="Send to"
              class="mb-4"
              :disabled="isUpdateNote"
              description="Select user to send message to"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @update:model-value="(value) => field.handleChange(value)"
              @change="(value) => setMessageReceiver(value)"
            />
          </template>
        </form.Field>
      </div>


      <div>
        <!-- attachments -->
        <div
          v-if="message.attachments.length"
          class="flex flex-wrap gap-4 my-4 transition-all duration-200 ease-in-out"
        >
          <div
            v-for="(file, index) in message.attachments"
            :key="file"
            class="relative w-[90px] h-[90px] rounded-lg border border-slate-200 flex items-center justify-center mt-2 bg-slate-100"
          >
            <img
              v-if="isImage(file)"
              :src="file"
              class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
              @click="expandImage(file)"
            >
            <div
              v-else
              class="flex items-center justify-center w-full h-full text-center bg-slate-200 rounded-lg cursor-pointer"
              @click="expandImage(file)"
            >
              <div class="text-dark font-weight-bold">
                {{ fileTypeToString(file) }}
              </div>
            </div>
            <span
              class="absolute top-0 right-0 p-1 flex justify-center items-center rounded-full border-transparent bg-black/50 hover:bg-black/75 transition duration-200 ease-in-out cursor-pointer overflow-hidden select-none size-[1.3rem]"
              @click="deleteAttachment(index)"
            >
              <span class="i-mdi-close text-white"></span>
            </span>
          </div>
        </div>

        <form.Field
          name="text"
          :validators="{
            onBlur: yup.string().required('Message is required'),
          }"
        >
          <template #default="{ field }">
            <BaseTextArea
              v-model="(field.state.value as string)"
              :name="field.name"
              :label="label"
              :placeholder="placeholder"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              :disabled="preview"
              :class="{ 'cursor-not-allowed opacity-50': preview }"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <div class="flex justify-between mt-4">
              <BaseButton
                type="button"
                text="Attach File"
                icon-class="i-mdi-paperclip"
                :disabled="isSubmitting || preview"
                :class="{ 'cursor-not-allowed opacity-50': isSubmitting || preview }"
                @click="open = true"
              />

              <div class="flex gap-4">
                <BaseButton
                  type="button"
                  variant="secondary"
                  text="Cancel"
                  :disabled="isSubmitting || preview"
                  :class="{ 'cursor-not-allowed opacity-50': isSubmitting || preview }"
                  @click="cancel"
                />

                <BaseButton
                  type="submit"
                  variant="primary"
                  text="Send"
                  :disabled="isSubmitting || !canSubmit || preview"
                  :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting || preview }"
                />
              </div>
            </div>
          </template>
        </form.Subscribe>
      </div>
    </form>
  </div>

  <Modal
    v-model="open"
    title="Upload Files"
    width="w-full md:w-1/2"
    close-button-text="Close"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    @close="setAttachments"
  >
    <template #body>
      <div class="mt-4">
        <AppFileUpload
          :storage-path="`${project.id}/documents/${messageType}-attachments/`"
          @on-file-upload-completed="fileUploadCompleted"
        />
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import BaseButton from '@/components/base/BaseButton.vue'
import BaseTextArea from '@/components/base/BaseTextArea.vue'
import { computed, PropType } from 'vue'
import AppFileUpload from '@/components/app/AppFileUpload.vue'
import Modal from '@/components/app/AppModal.vue'
import { Project } from '@/models/project'
import { ref } from 'vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import { Message, MESSAGE_TYPES } from '@/models/message'
import EmployeeAutoComplete from '@/components/base/EmployeeAutoComplete.vue'
import { fileTypeToString, isImage } from '@/utils'
import { useNotification } from '@kyvg/vue3-notification'
import StorageService from '@/services/StorageService'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { useAuth } from '@/composables/useAuth'

const props = defineProps({
  label: {
    type: String,
    default: 'Message'
  },
  placeholder: {
    type: String,
    default: 'Enter your message'
  },
  messageType: {
    type: String,
    required: true,
  },
  project: {
    type: Object as PropType<Project>,
    required: true,
  },
  saveFuntion: {
    type: Function,
    required: true
  },
  preview: {
    type: Boolean,
    default: false
  }
})

const { notify } = useNotification()
const storageService = new StorageService()
const { currentUser } = useAuth()
const message = ref(new Message())
const tempAttachments = ref<string[]>([])

const isPrivateMessageType = computed(()=> props.messageType === MESSAGE_TYPES.INTERNAL)
const isUpdateNote = ref(false)
const includeRealtor = ref(false)
const open = ref(false)

const form = useForm({
  defaultValues: {
    text: '',
    to: {
      id: ''
    },
    isUpdateNote: false
  },
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    if (currentUser.value.id) {
      message.value.from.id = currentUser.value.id
      message.value.from.name = currentUser.value.name
      message.value.from.email = currentUser.value.email
    }

    message.value.projectId = props.project.id
    message.value.type = props.messageType
    message.value.text = value.text

    message.value.includeRealtor = isUpdateNote.value && includeRealtor.value

    await props.saveFuntion(message.value)
    form.reset()
    message.value = new Message()
  }
})

const setMessageReceiver = (value: any) => {
  if (value) {
    message.value.to.id = value.id
    message.value.to.name = value.name
    message.value.to.email = value.email
  }
}


const setIsUpdateNote = (value: boolean, field: any) => {
  isUpdateNote.value = value
  field.handleChange(value)

  if (value) {
    message.value.to.id = ''
    message.value.to.name = ''
    message.value.to.email = ''
    form.setFieldValue('to.id', '')
  }
}

const cancel = () => {
  try {
    message.value.attachments.map(async (attachment) => {
      await storageService.deleteAttachmentWithStyledImages(attachment)
    })
    tempAttachments.value = []
  } catch (error) {
    console.log(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'Failed to delete attachments'
    })
  }

  message.value = new Message()
  form.reset()
}

const setAttachments = () => {
  open.value = false
  message.value.attachments = tempAttachments.value
}

const fileUploadCompleted = (urls: string[]) => {
  tempAttachments.value = urls
}

const expandImage = (imageURL: string) => {
  window.open(imageURL, '_blank')
}

const deleteAttachment = async (index: number) => {
  const attachment = message.value.attachments[index]
  try {
    await storageService.deleteAttachmentWithStyledImages(attachment)
    message.value.attachments.splice(index, 1)
    tempAttachments.value = tempAttachments.value.filter(att => att !== attachment)
  } catch (error) {
    console.log(error)
    notify({
      type: 'error',
      title: 'Error',
      text: 'Failed to delete attachment'
    })
  }
}
</script>
